import { memo, useMemo } from "react"
import { Container, Heading, Box, HStack } from "@chakra-ui/react"
import { useImage } from "@app/hooks/useImage"
import { useSection } from "@app/hooks/useSection"
import { Navigation } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import { CarouselOverflowWrapper, extraOverflowParentProps } from "@app/components/CarouselOverflowWrapper"
import { CustomButton } from "@app/components/Button"
import { NutritionistItem } from "@components/Nutritionist/NutritionistItem"
import { useRoutes } from "@app/hooks/useRoutes"

const FeaturedNutritionists: React.FC<GatsbyTypes.SanitySectionFeaturedNutritionists> = ({
  title,
  nutritionists,
  analytics,
  bgColor,
  button,
}) => {
  const { getGatsbyImage } = useImage()
  const { handleTrackingClick } = useSection({ analytics })
  const { urlResolver } = useRoutes()

  const items = useMemo(
    () =>
      nutritionists?.map((nutritionist: any) => ({
        ...nutritionist,
        key: nutritionist?._id,
        title: nutritionist?.title,
        link: urlResolver(nutritionist)?.url,
        image: getGatsbyImage(nutritionist?.image),
      })),
    [getGatsbyImage, nutritionists, urlResolver]
  )

  return (
    <Box as="section" bgColor={bgColor} py={[9, 28]} {...extraOverflowParentProps}>
      <Container>
        <HStack justifyContent={["center", "space-between"]} alignItems="flex-start" spacing={4}>
          {title && (
            <Heading as="p" size="h2" mb={[4, 8]} textAlign={["center", "left"]} fontSize={["2xl", "8xl"]} lineHeight={1}>
              {title}
            </Heading>
          )}
          <CustomButton button={button} linkStyles={{ display: ["none", "block"] }} buttonStyles={{ h: 12 }} />
        </HStack>
        <CarouselOverflowWrapper type="articles">
          <Swiper
            modules={[Navigation]}
            navigation
            pagination={{ clickable: true }}
            slidesPerView="auto"
            centeredSlides
            breakpoints={{
              600: {
                centeredSlides: false,
              },
            }}
          >
            {items?.map((nutritionist: any) => (
              <SwiperSlide key={nutritionist?.key}>
                <Box maxW={80} w={80} h="full" bg={["brand.offWhite", "unset"]} p={[2, 0]}>
                  <NutritionistItem nutritionist={nutritionist} handleTrackingClick={handleTrackingClick} />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </CarouselOverflowWrapper>
        <CustomButton
          button={button}
          linkStyles={{
            display: ["block", "none"],
            mt: 6,
          }}
          buttonStyles={{ h: [12] }}
          width="full"
        />
      </Container>
    </Box>
  )
}

export default memo(FeaturedNutritionists)
